import Image from 'next/image'

import { getImageDimensions } from '@sanity/asset-utils'
import { stegaClean } from 'next-sanity'

import { TrackingLocation } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { Review as ReviewType } from '@/sanity.types'
import { urlForImage } from '@/sanity/lib/utils'

export type ReviewProps = {
  value: ReviewType & { _key?: string }
  tracking: TrackingLocation
  className?: string
}

export const Review = ({ value, tracking, className }: ReviewProps) => {
  if (!value) return null

  const quote = stegaClean(value.quote)
  const author = stegaClean(value.author)
  const bgColor = stegaClean(value.bgColor) || 'green'
  const imageBaseUrl = urlForImage(value.picture)?.url()

  const dimensions = value.picture?.asset?._ref
    ? getImageDimensions(value.picture.asset._ref)
    : null

  // Display at half native size for retina sharpness
  const nativeWidth = dimensions?.width || 256
  const nativeHeight = dimensions?.height || 256
  const displayWidth = nativeWidth / 2
  const displayHeight = nativeHeight / 2

  return (
    <div
      className={cn(
        'rounded-lg flex flex-col gap-md items-center justify-center p-4xl',
        bgColor === 'white'
          ? 'bg-white-default'
          : bgColor === 'purple'
            ? 'bg-purple-600'
            : 'bg-green-600',
        className,
      )}
    >
      {imageBaseUrl && (
        <div
          className="relative overflow-hidden shrink-0"
          style={{ width: displayWidth, height: displayHeight }}
        >
          <Image
            loader={() => {
              // Load at native size (2x of display size for retina)
              return urlForImage(value.picture)?.width(nativeWidth).url() || imageBaseUrl
            }}
            src={imageBaseUrl}
            alt={value.picture?.alt || author || 'Reviewer'}
            width={displayWidth}
            height={displayHeight}
            className="object-cover w-full h-full"
            sizes={`${displayWidth}px`}
            loading="lazy"
          />
        </div>
      )}

      {quote && (
        <blockquote
          className={cn(
            'size-header-h5 lg:size-header-h4 text-center whitespace-pre-line',
            bgColor === 'white' ? 'text-white-950' : 'text-white-default',
          )}
        >
          {quote}
        </blockquote>
      )}

      {author && (
        <cite
          className={cn(
            'size-body-md not-italic text-center py-md whitespace-pre-line',
            bgColor === 'white' ? 'text-white-950' : 'text-green-100',
          )}
        >
          {author}
        </cite>
      )}
    </div>
  )
}
